<template>
    <div>
        <form class="login-form form" @submit.prevent="login">
            <div class="form__row">
                <label class="label">{{ $t('form_inputs.email_address_label') }}</label>
                <input
                    id="email"
                    v-model.trim="credentials.email"
                    type="email"
                    class="input"
                    :class="{
                        'input--error': $v.credentials.email.$invalid && $v.credentials.email.$dirty,
                    }"
                />
                <template v-if="$v.credentials.email.$dirty">
                    <InputError v-if="!$v.credentials.email.required" static-position>
                        {{ $t('errors.field_required') }}
                    </InputError>
                    <InputError v-if="!$v.credentials.email.email" static-position>
                        {{ $t('errors.field_invalid') }}
                    </InputError>
                </template>
            </div>

            <div class="form__row">
                <label class="label">{{ $t('form_inputs.password_label') }}</label>
                <password-input
                    v-model.trim="credentials.password"
                    :has-error="$v.credentials.password.$invalid && $v.credentials.password.$dirty"
                />
                <InputError v-if="!$v.credentials.password.required && $v.credentials.password.$dirty" static-position>
                    {{ $t('errors.field_required') }}
                </InputError>
                <div class="login-form__forgot-password">
                    <ModalLink v-if="showForgotPasswordInModal" to="forgot-password">
                        {{ $t('modals.login.forgot_password_link') }}
                    </ModalLink>
                    <a v-else @click="$emit('toggle-forgot-password')">
                        {{ $t('modals.login.forgot_password_link') }}
                    </a>
                </div>
            </div>

            <div v-if="twoFactorAuthRequired" class="form__row">
                <label class="label">{{ $t('form_inputs.2fa_code_label') }}</label>
                <input
                    id="2faCode"
                    ref="twoFactorCodeInput"
                    v-model.trim="twoFactorAuthCode"
                    type="text"
                    class="input"
                    :class="{
                        'input--error': $v.twoFactorAuthCode.$invalid && $v.twoFactorAuthCode.$dirty,
                    }"
                />
                <InputHint>
                    {{ $t('form_inputs.2fa_code_hint') }}
                </InputHint>
                <InputError v-if="!$v.twoFactorAuthCode.required && $v.twoFactorAuthCode.$dirty" static-position>
                    {{ $t('errors.field_required') }}
                </InputError>
            </div>

            <div class="form__row">
                <Notice v-if="loginError" type="warning">
                    <template v-if="loginError === 'too_many_login_attempts'">
                        {{ $t('errors.too_many_login_attempts') }}
                    </template>
                    <template v-else>
                        <p>
                            {{ $t('errors.login_failed') }}
                            {{ $t('errors.try_again_or_contact_us') }}
                        </p>
                        <p v-if="loginError === 'invalid_credentials' && $te('errors.login_failed_forgot_password')">
                            {{ $t('errors.login_failed_forgot_password') }}
                            <ModalLink v-if="showForgotPasswordInModal" to="forgot-password">
                                {{ $t('modals.login.forgot_password_link') }}
                            </ModalLink>
                            <a v-else @click="$emit('toggle-forgot-password')">
                                {{ $t('common.click_here') }}
                            </a>
                        </p>
                    </template>
                </Notice>
                <button :disabled="loggingIn" class="button button--primary button--full-width">
                    <template v-if="!loggingIn">
                        {{ $t('buttons.login') }}
                    </template>
                    <LoadingIndicator v-if="loggingIn" />
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, email, requiredIf } from 'vuelidate/lib/validators';
import { actionTypes as userActions } from '~/store/user';
import Notice from '~/components/Notice.vue';
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import PasswordInput from '~/components/onboarding/PasswordInput.vue';
import ModalLink from '~/components/modals/ModalLink.vue';
import InputError from '~/components/form-elements/InputError.vue';

const TWO_FACTOR_AUTH_REQUIRED = 'required_2fa_code';
const INVALID_CREDENTIALS = 'invalid_credentials';

export default {
    components: {
        Notice,
        LoadingIndicator,
        PasswordInput,
        ModalLink,
        InputError,
        InputHint: () => import('~/components/form-elements/InputHint.vue'),
    },
    mixins: [validationMixin],
    validations: {
        credentials: {
            email: { required, email },
            password: { required },
        },
        twoFactorAuthCode: {
            required: requiredIf(function () {
                return this.twoFactorAuthRequired;
            }),
        },
    },
    props: {
        email: {
            type: String,
            default: null,
        },
        showForgotPasswordInModal: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            credentials: {
                email: this.email,
                password: null,
            },
            twoFactorAuthCode: null,
            twoFactorAuthRequired: false,
            loginError: null,
            loggingIn: false,
        };
    },
    computed: {
        ...mapGetters(['user']),
    },
    methods: {
        dispatchLoginEvent() {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'login',
                    method: 'E-Mail',
                    locale: this.$i18n.locale,
                    user_id: this.user.id,
                });
            }
        },
        login() {
            this.$v.credentials.$touch();

            if (this.$v.credentials.$invalid) {
                return;
            }

            if (this.twoFactorAuthRequired) {
                this.$v.twoFactorAuthCode.$touch();

                if (this.$v.twoFactorAuthCode.$invalid) {
                    return;
                }
            }

            this.loggingIn = true;
            this.loginError = null;

            const payload = this.getPayload();

            this.$store
                .dispatch(userActions.LOGIN, payload)
                .then(() => {
                    this.dispatchLoginEvent();
                    this.$emit('logged-in');
                })
                .catch(error => {
                    this.handleLoginError(error);
                })
                .finally(() => {
                    this.loggingIn = false;
                });
        },
        getPayload() {
            const payload = {
                ...this.credentials,
            };

            if (this.twoFactorAuthRequired) {
                payload.twoFactorAuthCode = this.twoFactorAuthCode;
            }

            return payload;
        },
        handleLoginError(error) {
            if (error.slug === TWO_FACTOR_AUTH_REQUIRED) {
                this.show2FA();

                return;
            }

            if (error.slug !== INVALID_CREDENTIALS) {
                this.reportToSentry(error);
            }

            this.loginError = error.slug;
        },
        show2FA() {
            this.twoFactorAuthRequired = true;

            // Allows the password manager to autofill and login automatically
            setTimeout(() => {
                this.$refs.twoFactorCodeInput.focus();
            }, 0);
        },
        reportToSentry(error) {
            this.$sentry.withScope(scope => {
                scope.setExtra('error', error);

                this.$sentry.captureMessage(`Login failed with error: ${JSON.stringify(error)}`, 'warning');
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.login-form {
    &__forgot-password {
        margin-top: 20px;
        text-align: left;
    }

    .notice {
        margin: 20px 0;
        text-align: left;
    }
}
</style>
